import Vue from "vue";
import axios from "@axios";
import store from "@/store";
import {useUtils as useI18nUtils} from "@core/libs/i18n";

const MAX_CONFIG_TABS = 5;
const DEFAULT_TAB_PREFIX = "Tab";

const getDefaultConfigTab = (order) => ({
  id: `tab_${order}`,
  name: `${DEFAULT_TAB_PREFIX} ${order}`,
  id_layout: null,
  layoutItems: [],
});

export default {
  namespaced: true,

  state: {
    alarmViews: [],

    slotData: null,
    slotSelection: false,

    configTabs: [getDefaultConfigTab(1)],
    activeConfigTabId: "tab_1",
  },
  getters: {
    layoutItems(state) {
      const tab = state.configTabs.find((t) => t.id === state.activeConfigTabId);
      return tab ? tab.layoutItems : [];
    },
    configTabs(state) {
      return state.configTabs;
    },
    activeConfigTabId(state) {
      return state.activeConfigTabId;
    },
    activeConfigTab(state) {
      return state.configTabs.find((t) => t.id === state.activeConfigTabId) || null;
    },
    canAddConfigTab(state) {
      return state.configTabs.length < MAX_CONFIG_TABS;
    },
    maxConfigTabs() {
      return MAX_CONFIG_TABS;
    },
  },
  mutations: {
    setAlarmViews(state, value) {
      state.setAlarmViews = value;
    },

    setSlotData(state, value) {
      state.slotData = value;
    },
    setSlotSelection(state, value) {
      state.slotSelection = value;
    },
    setConfigTabs(state, value) {
      state.configTabs = Array.isArray(value) && value.length > 0
        ? value
        : [getDefaultConfigTab(1)];
      if (!state.configTabs.some((t) => t.id === state.activeConfigTabId)) {
        state.activeConfigTabId = state.configTabs[0].id;
      }
    },
    setActiveConfigTabId(state, value) {
      if (state.configTabs.some((t) => t.id === value)) {
        state.activeConfigTabId = value;
      }
    },
    addConfigTab(state) {
      if (state.configTabs.length >= MAX_CONFIG_TABS) return;
      const order = state.configTabs.length + 1;
      const newTab = getDefaultConfigTab(order);
      state.configTabs.push(newTab);
      state.activeConfigTabId = newTab.id;
    },
    removeConfigTab(state, tabId) {
      const index = state.configTabs.findIndex((t) => t.id === tabId);
      if (index < 0 || state.configTabs.length <= 1) return;
      state.configTabs.splice(index, 1);
      state.configTabs.forEach((tab, i) => {
        tab.name = `${DEFAULT_TAB_PREFIX} ${i + 1}`;
        tab.id = `tab_${i + 1}`;
      });
      if (state.activeConfigTabId === tabId) {
        state.activeConfigTabId = state.configTabs[Math.max(0, index - 1)].id;
      } else {
        const stillExists = state.configTabs.find((t) => t.id === state.activeConfigTabId);
        if (!stillExists) state.activeConfigTabId = state.configTabs[0].id;
      }
    },
    setLayoutItem(state, value) {
      const tab = state.configTabs.find((t) => t.id === state.activeConfigTabId);
      if (!tab) return;
      const type = !value.data && value.type === "cam" ? null : value.type;
      tab.layoutItems.push({ ...value, type });
    },
    emptyLayoutItems(state) {
      const tab = state.configTabs.find((t) => t.id === state.activeConfigTabId);
      if (tab) tab.layoutItems = [];
    },
    setLayoutItems(state, value) {
      state.configTabs = [{
        ...getDefaultConfigTab(1),
        layoutItems: Array.isArray(value) ? [...value] : [],
      }];
      state.activeConfigTabId = state.configTabs[0].id;
    },
    setLayoutItemsForConfigTab(state, { tabId, layoutItems }) {
      const tab = state.configTabs.find((t) => t.id === tabId);
      if (tab) tab.layoutItems = Array.isArray(layoutItems) ? [...layoutItems] : [];
    },
    setConfigTabLayout(state, { tabId, id_layout }) {
      const tab = state.configTabs.find((t) => t.id === tabId);
      if (tab) tab.id_layout = id_layout;
    },
    removeLayoutItem(state, value) {
      const tab = state.configTabs.find((t) => t.id === state.activeConfigTabId);
      if (!tab) return;
      const i = tab.layoutItems.map((item) => item.pos).indexOf(value);
      if (i >= 0) tab.layoutItems.splice(i, 1);
    },
    // Slots snapshot their filters when placed, so a later Accept has to reach
    // the ones already in the layout, in every tab.
    applySlotFiltersBySource(state, { source, filters }) {
      state.configTabs.forEach((tab) => {
        (tab.layoutItems || []).forEach((item) => {
          if (item.data?.source === source) {
            Vue.set(item.data, "filters", { ...filters });
          }
        });
      });
      if (state.slotData?.data?.source === source) {
        Vue.set(state.slotData.data, "filters", { ...filters });
      }
    },
    clear(state) {
      state.alarmViews = [];
      state.slotData = null;
      state.slotSelection = false;
      state.configTabs = [getDefaultConfigTab(1)];
      state.activeConfigTabId = "tab_1";
    },
  },
  actions: {
    async export({commit}, params) {
      const response = await axios.get("v1/services/views/get-views", {
        params,
        responseType: params?.action == "export" ? "blob" : "json",
      });
      if (!response.data) {
        return [];
      }
      if (params?.action == "export") {
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          response.headers["content-disposition"].split("filename=")[1]
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        const {t} = useI18nUtils();

        const notification = {
          id: response.headers["content-disposition"].split("filename=")[1],
          progress: 100,
          completed: true,
          view: "Vista de alarmas",
          url: url,
          download: response.headers["content-disposition"].split("filename=")[1],
          title: "Reporte de Vista de alarmas",
          body: "Descarga completa",
          error: false,
          timestamp:
            new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
        };
        store.dispatch("grid/updateDownloadNotification", notification);
      } else {
        commit("setData", response.data.data);
        return response.data;
      }
    },
    async getAlarmViews({ commit }, params) {
      const { data } = await axios.get('v1/services/views/get-views', { params })
      commit('setAlarmViews', data.data);

      if (!data.success) {
        return [];
      }

      return data;
    },

    // Layout -----------------------------
    addLayoutItem({ state, commit }, data) {
      commit('removeLayoutItem', data.pos);
      commit('setLayoutItem', data);
    },
    emptyLayoutItems({ commit }) {
      commit('emptyLayoutItems');
    },
    setSlotData({ commit }, data) {
      commit('setSlotData', data);
      commit('setSlotSelection', true);
    },
    removeLayoutItem({ commit }, data) {
      commit('removeLayoutItem', data);
    },
    applySlotFiltersBySource({ commit }, payload) {
      commit('applySlotFiltersBySource', payload);
    },
    resetSlotSelection({ commit }) {
      commit("setSlotData", null);
      commit("setSlotSelection", false);
    },
    setConfigTabs({ commit }, value) {
      commit("setConfigTabs", value);
    },
    setActiveConfigTabId({ commit }, value) {
      commit("setActiveConfigTabId", value);
    },
    addConfigTab({ commit }) {
      commit("addConfigTab");
    },
    removeConfigTab({ commit }, tabId) {
      commit("removeConfigTab", tabId);
    },
    setConfigTabLayout({ commit }, payload) {
      commit("setConfigTabLayout", payload);
    },
    clear({ commit }) {
      commit("clear");
    },
  },
}
